import type { CollegeCourseItem } from "@zrt-ai-office/types/course"
import "./bootcamp-card.less"

interface BootcampCardProps {
  course: CollegeCourseItem
  onClick?: (id: number) => void
}

/* ── Placeholder images for courses without covers ── */
const COURSE_PLACEHOLDERS = [
  `${import.meta.env.BASE_URL}images/academy/course-placeholder-1.png`,
  `${import.meta.env.BASE_URL}images/academy/course-placeholder-2.png`,
  `${import.meta.env.BASE_URL}images/academy/course-placeholder-3.png`,
]

function titleHash(title: string): number {
  let hash = 0
  for (let i = 0; i < title.length; i++) {
    hash = ((hash << 5) - hash + title.charCodeAt(i)) | 0
  }
  return Math.abs(hash)
}

/** Pick a deterministic placeholder image based on course title */
function getPlaceholderImage(title: string): string {
  return COURSE_PLACEHOLDERS[titleHash(title) % COURSE_PLACEHOLDERS.length]
}

/* ── Standard card: image + title + desc ── */
export function BootcampCard({ course, onClick }: BootcampCardProps) {
  const isDisabled = course.status === 0

  return (
    <div
      aria-disabled={isDisabled || undefined}
      className={`bootcamp-card bootcamp-card--standard ${isDisabled ? 'bootcamp-card--disabled' : 'cursor-pointer'}`}
      onClick={isDisabled ? undefined : () => onClick?.(course.id)}
      role={isDisabled ? undefined : "button"}
      tabIndex={isDisabled ? undefined : 0}
      onKeyDown={isDisabled ? undefined : (e) => { if (e.key === 'Enter') onClick?.(course.id) }}
    >
      {/* Image */}
      <div className="bootcamp-card__image">
        <img
          src={course.coverImage || getPlaceholderImage(course.title)}
          alt={course.title}
          width={320}
          height={180}
        />
        {isDisabled && <span className="bootcamp-card__status-label">待上架</span>}
      </div>

      {/* Body */}
      <div className="bootcamp-card__body">
        <h3 className="bootcamp-card__title bootcamp-card__title--standard">
          {course.title}
        </h3>
        <p className="bootcamp-card__desc bootcamp-card__desc--standard">
          {course.intro || course.description}
        </p>
      </div>
    </div>
  )
}
